import TonalButton from '@/components/TonalButton';
import addHighContrastStyles from '@/lib/styling/addHighContrastStyles';
import styled from '@emotion/styled';
import {
  type ButtonProps,
  CircularProgress,
  darken,
  lighten,
} from '@mui/material';
import {
  AttachFile as AddFileIcon,
  WarningOutlined as WarningIcon,
} from '@mui/icons-material';
import ErrorIcon from '../utils/icons/ErrorIcon';
import { common } from '@mui/material/colors';
import stripProps from '@/lib/utils/stripProps';
import { type SavingState } from '../../getSavingState';
import AddFileButtonLabel from './ButtonLabel';

const ButtonWrapper = styled('li')({
  marginBottom: 8,
});

type StyledButtonProps = ButtonProps & {
  hasError: boolean;
};

const StyledButton = styled(
  stripProps(TonalButton, 'hasError'),
)<StyledButtonProps>(
  {
    height: 32,
  },
  ({ theme, hasError }) => {
    if (hasError) {
      return {
        color: theme.isDark
          ? lighten(theme.palette.error.light, 0.1)
          : theme.palette.error.dark,
        backgroundColor: theme.isDark
          ? theme.palette.error.dark
          : lighten(theme.palette.error.light, 0.8),
        ':hover': {
          color: theme.isDark
            ? lighten(theme.palette.error.light, 0.3)
            : undefined,
          backgroundColor: theme.isDark
            ? darken(theme.palette.error.main, 0.2)
            : lighten(theme.palette.error.light, 0.7),
        },
      };
    }
  },
  addHighContrastStyles({
    height: 36,
  }),
  addHighContrastStyles(({ theme, hasError }) => {
    if (hasError) {
      return {
        color: theme.isDark ? common.black : theme.palette.text.primary,
        ':hover': {
          color: theme.isDark ? common.black : theme.palette.text.primary,
        },
        '.MuiSvgIcon-root, .MuiCircularProgress-root': {
          color: theme.isDark ? common.black : undefined,
        },
      };
    }
  }),
);

const PartialLoadingIndicator = styled(CircularProgress)(
  ({ theme, color }) => ({
    marginLeft: 2,
    marginRight: 2,
    color:
      typeof color === 'undefined' ? theme.palette.text.secondary : undefined,
  }),
);

type AddFileButtonProps = Omit<
  ButtonProps,
  'startIcon' | 'loadingPosition' | 'children'
> & {
  savingState: SavingState;
  loadingPartial: boolean;
  hasError: boolean;
  hasWarning: boolean;
};

const AddFileButton = ({
  savingState,
  loadingPartial,
  hasError,
  hasWarning,
  ...props
}: AddFileButtonProps) => (
  <ButtonWrapper>
    <StyledButton
      startIcon={
        loadingPartial ? (
          <PartialLoadingIndicator
            color={hasError ? 'error' : hasWarning ? 'warning' : undefined}
            size={16}
          />
        ) : hasError ? (
          <ErrorIcon />
        ) : hasWarning ? (
          <WarningIcon color="warning" />
        ) : (
          <AddFileIcon />
        )
      }
      hasError={hasError}
      {...props}
    >
      <AddFileButtonLabel savingState={savingState} />
    </StyledButton>
  </ButtonWrapper>
);

export default AddFileButton;
